import { useRef, useCallback } from 'react';
import { findNextFocusableElement } from './findNextFocusableElement.js';
import { T as Tab, A as ArrowUp, a as ArrowRight, b as ArrowDown, c as ArrowLeft } from './keys-BQYfE7yD.js';
import { keyboardMatch } from './match.js';

const TEXT_FIELD_SELECTOR = 'textarea, input:not([type="checkbox"]):not([type="radio"]), [contenteditable="true"]';
const getNavigationDirection = (event, mode = 'main') => {
    if (mode === 'list' && keyboardMatch(event, Tab)) {
        return event.shiftKey ? 'up' : 'down';
    }
    if (keyboardMatch(event, ArrowUp)) {
        return 'up';
    }
    if (keyboardMatch(event, ArrowRight)) {
        return 'right';
    }
    if (keyboardMatch(event, ArrowDown)) {
        return 'down';
    }
    if (keyboardMatch(event, ArrowLeft)) {
        return 'left';
    }
    return null;
};
const handleKeyboardNavigation = (event, container, activeElementOverride = null, mode = 'main') => {
    const activeElement = activeElementOverride ?? document.activeElement;
    const direction = getNavigationDirection(event, mode);
    if (!activeElement?.matches(TEXT_FIELD_SELECTOR) && direction !== null) {
        const nextFocusableElement = findNextFocusableElement(container, activeElement, direction, mode);
        if (nextFocusableElement !== null) {
            event.preventDefault();
            nextFocusableElement.focus();
        }
    }
};
/**
 * Хук для клавиатурной навигации внутри контейнера с использованием клавиш-стрелок и Tab.
 * Поведение зависит от параметра `mode`:
 *  `main` — поддерживается навигация стрелками, поведение Tab не переопределяется
 *  `list` — поддерживается навигация стрелками, Tab и Shift+Tab работают аналогично ↓ и ↑, навигация зациклена
 */
const useKeyboardNavigation = (args = {}) => {
    const { mode = 'main', enabled = true, activeElement: activeElementOverride } = args;
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    const activeElementOverrideRef = useRef(activeElementOverride);
    activeElementOverrideRef.current = activeElementOverride;
    const modeRef = useRef(mode);
    modeRef.current = mode;
    const bindNavigationRef = useCallback((container) => {
        if (!container) {
            return;
        }
        const handleKeyDown = (event) => {
            if (enabledRef.current) {
                handleKeyboardNavigation(event, container, activeElementOverrideRef.current, modeRef.current);
            }
        };
        container.addEventListener('keydown', handleKeyDown);
    }, []);
    return bindNavigationRef;
};

export { TEXT_FIELD_SELECTOR, useKeyboardNavigation };
//# sourceMappingURL=useKeyboardNavigation.js.map
